import { useCallback, useMemo } from 'react';

import { useSportContext } from '@sorare/core/src/contexts/sport';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';

import type { Mode } from '#src/lib/gameMode.ts';

import {
  getArenaModeState,
  getProModeState,
  mergeBoardViewUpdate,
  normalizeLastVisitedBoardView,
  toFlatView,
  type BoardViewUpdate,
} from './lastVisitedBoardView.ts';

export const useLastVisitedBoardView = () => {
  const { lifecycle, update, loading } = useLifecycle();
  const { sport } = useSportContext();

  const rawLastVisitedBoardView = lifecycle?.[LIFECYCLE.lastVisitedBoardViewV2];
  const allViews = useMemo(
    () => normalizeLastVisitedBoardView(rawLastVisitedBoardView),
    [rawLastVisitedBoardView]
  );
  const sportView = allViews?.[sport];
  const lastVisitedView = toFlatView(sportView);

  const getArenaView = useCallback(
    (mode: Mode) => getArenaModeState(sportView, mode),
    [sportView]
  );

  const getProView = useCallback(() => getProModeState(sportView), [sportView]);

  const updateBoardView = useCallback(
    (partial: BoardViewUpdate) => {
      if (loading) return;
      const nextSportView = mergeBoardViewUpdate(sportView, partial);
      update(LIFECYCLE.lastVisitedBoardViewV2, {
        ...allViews,
        [sport]: nextSportView,
      });
    },
    [allViews, sportView, loading, sport, update]
  );

  return {
    lastVisitedView,
    getArenaView,
    getProView,
    updateBoardView,
    loading,
  };
};
